import type { JSX, ReactNode } from 'react';
import { parseAnswer } from '@/lib/answer-markdown';
import type { Inline } from '@/lib/answer-markdown';

/**
 * The answer as the bot shows it in Telegram: headings, bold labels, ids in
 * mono, and list rows — rather than the Markdown source with its asterisks.
 * `trailing` (the chat's typing caret) is set at the end of the last block.
 */
export function AnswerText({
  text,
  trailing,
}: {
  text: string;
  trailing?: ReactNode;
}): JSX.Element {
  const blocks = parseAnswer(text);
  const end = (i: number): ReactNode => (i === blocks.length - 1 ? trailing : null);
  return (
    <div className="flex flex-col gap-2.5 text-sm leading-[1.6] [overflow-wrap:anywhere]">
      {blocks.length === 0 && trailing}
      {blocks.map((block, i) => {
        if (block.kind === 'heading') {
          return (
            <div key={i} className="mt-1 text-[15px] font-semibold first:mt-0">
              <Spans parts={block.inline} />
              {end(i)}
            </div>
          );
        }
        if (block.kind === 'list') {
          return (
            <ul key={i} className="flex flex-col gap-1">
              {block.items.map((item, j) => (
                <li key={j} className="flex gap-2">
                  <span className="text-muted-foreground select-none" aria-hidden>
                    •
                  </span>
                  <span className="min-w-0">
                    <Spans parts={item} />
                    {j === block.items.length - 1 && end(i)}
                  </span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="whitespace-pre-wrap">
            <Spans parts={block.inline} />
            {end(i)}
          </p>
        );
      })}
    </div>
  );
}

function Spans({ parts }: { parts: Inline[] }): JSX.Element {
  return (
    <>
      {parts.map((part, i) =>
        part.kind === 'bold' ? (
          <strong key={i} className="font-semibold">
            {part.text}
          </strong>
        ) : part.kind === 'code' ? (
          <code key={i} className="bg-muted rounded px-1 py-0.5 font-mono text-[13px]">
            {part.text}
          </code>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </>
  );
}
